// provider === component
import { createContext, useEffect } from "react";
import { defaultSettings } from "../config";
import useLocalStorage from "../hooks/useLocalStorage";
import getColorPresets, {
  defaultPreset,
  colorPresets,
} from "../utils/getColorPresets";

const initialState = {
  ...defaultSettings,

  // Mode
  onToggleMode: () => {},
  onChangeMode: () => {},

  // Direction
  onToggleDirection: () => {},
  onChangeDirection: () => {},
  onChangeDirectionByLang: () => {},

  // Layout
  onToggleLayout: () => {},
  onChangeLayout: () => {},

  // Contrast
  onToggleContrast: () => {},
  onChangeContrast: () => {},

  // Color
  onChangeColor: () => {},
  setColor: defaultPreset,
  colorOption: [],

  // Chat Colors
  themeSenderChatColor: '#0162C4', // Default sender chat bubble color
  themeReceiverChatColor: '#E0E0E0', // Default receiver chat bubble color
  onChangeSenderChatColor: () => {},
  onChangeReceiverChatColor: () => {},

  // Chat Font Sizes
  themeSenderFontSize: 14, // Default sender chat font size
  themeReceiverFontSize: 14, // Default receiver chat font size
  onChangeSenderFontSize: () => {},
  onChangeReceiverFontSize: () => {},

  // Stretch
  onToggleStretch: () => {},

  // Reset
  onResetSetting: () => {},
};

const SettingsContext = createContext(initialState);

const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useLocalStorage("settings", {
    themeMode: initialState.themeMode,
    themeLayout: initialState.themeLayout,
    themeStretch: initialState.themeStretch,
    themeContrast: initialState.themeContrast,
    themeDirection: initialState.themeDirection,
    themeColorPresets: initialState.themeColorPresets,
    themeSenderChatColor: initialState.themeSenderChatColor, // Add to local storage
    themeReceiverChatColor: initialState.themeReceiverChatColor, // Add to local storage
    themeSenderFontSize: initialState.themeSenderFontSize, // Add to local storage
    themeReceiverFontSize: initialState.themeReceiverFontSize, // Add to local storage
  });

  const isArabic = localStorage.getItem("i18nextLng") === "ar";

  useEffect(() => {
    if (isArabic) {
      onChangeDirectionByLang("ar");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isArabic]);

  // Mode

  const onToggleMode = () => {
    setSettings({
      ...settings,
      themeMode: settings.themeMode === "light" ? "dark" : "light",
    });
  };

  const onChangeMode = (event) => {
    setSettings({
      ...settings,
      themeMode: event.target.value,
    });
  };

  // Direction

  const onToggleDirection = () => {
    setSettings({
      ...settings,
      themeDirection: settings.themeDirection === "rtl" ? "ltr" : "rtl",
    });
  };

  const onChangeDirection = (event) => {
    setSettings({
      ...settings,
      themeDirection: event.target.value,
    });
  };

  const onChangeDirectionByLang = (lang) => {
    setSettings({
      ...settings,
      themeDirection: lang === "ar" ? "rtl" : "ltr",
    });
  };

  // Layout

  const onToggleLayout = () => {
    setSettings({
      ...settings,
      themeLayout:
        settings.themeLayout === "vertical" ? "horizontal" : "vertical",
    });
  };

  const onChangeLayout = (event) => {
    setSettings({
      ...settings,
      themeLayout: event.target.value,
    });
  };

  // Contrast

  const onToggleContrast = () => {
    setSettings({
      ...settings,
      themeContrast: settings.themeContrast === "default" ? "bold" : "default",
    });
  };

  const onChangeContrast = (event) => {
    setSettings({
      ...settings,
      themeContrast: event.target.value,
    });
  };

  // Color

  const onChangeColor = (event) => {
    setSettings({
      ...settings,
      themeColorPresets: event.target.value,
    });
  };

  // Chat Colors
  const onChangeSenderChatColor = (color) => {
    setSettings({
      ...settings,
      themeSenderChatColor: color,
    });
  };

  const onChangeReceiverChatColor = (color) => {
    setSettings({
      ...settings,
      themeReceiverChatColor: color,
    });
  };

  // Chat Font Sizes
  const onChangeSenderFontSize = (size) => {
    setSettings({
      ...settings,
      themeSenderFontSize: size,
    });
  };

  const onChangeReceiverFontSize = (size) => {
    setSettings({
      ...settings,
      themeReceiverFontSize: size,
    });
  };

  // Stretch

  const onToggleStretch = () => {
    setSettings({
      ...settings,
      themeStretch: !settings.themeStretch,
    });
  };

  // Reset

  const onResetSetting = () => {
    setSettings({
      themeMode: initialState.themeMode,
      themeLayout: initialState.themeLayout,
      themeStretch: initialState.themeStretch,
      themeContrast: initialState.themeContrast,
      themeDirection: initialState.themeDirection,
      themeColorPresets: initialState.themeColorPresets,
      themeSenderChatColor: initialState.themeSenderChatColor, // Reset chat colors
      themeReceiverChatColor: initialState.themeReceiverChatColor, // Reset chat colors
      themeSenderFontSize: initialState.themeSenderFontSize, // Reset chat font sizes
      themeReceiverFontSize: initialState.themeReceiverFontSize, // Reset chat font sizes
    });
  };

  return (
    <SettingsContext.Provider
      value={{
        ...settings, // Mode
        onToggleMode,
        onChangeMode,

        // Direction
        onToggleDirection,
        onChangeDirection,
        onChangeDirectionByLang,

        // Layout
        onToggleLayout,
        onChangeLayout,

        // Contrast
        onChangeContrast,
        onToggleContrast,

        // Stretch
        onToggleStretch,

        // Color
        onChangeColor,
        setColor: getColorPresets(settings.themeColorPresets),
        colorOption: colorPresets.map((color) => ({
          name: color.name,
          value: color.main,
        })),

        // Chat Colors
        onChangeSenderChatColor,
        onChangeReceiverChatColor,

        // Chat Font Sizes
        onChangeSenderFontSize,
        onChangeReceiverFontSize,

        // Reset
        onResetSetting,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export {SettingsContext};

export default SettingsProvider;